import { Box, IconButton, makeStyles } from '@material-ui/core';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import clsx from 'clsx';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { BaseCheckbox } from '../BaseFilters/BaseCheckbox';
import {
  CheckboxTreeContextProvider,
  useCheckboxTreeContext,
} from './CheckboxTreeContext';
import { useCheckboxTree } from './hooks/useCheckboxTree';
import {
  CheckboxNodeWithParent,
  CheckboxTreeItemProps,
  CheckboxTreeProps,
} from './types';
import { filter, handleCheck } from './utils';

const useStyles = makeStyles(
  theme => ({
    itemChildren: {
      marginLeft: theme.spacing(3),
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    expandIcon: {
      transform: 'rotate(0deg)',
      '&$expanded': {
        transform: 'rotate(90deg)',
      },
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    childless: {
      visibility: 'hidden',
    },
    expanded: {},
    icon: {
      fontSize: '16px',
      color: theme.palette.secondary.main,
    },
  }),
  { name: 'CheckboxTree' },
);

function CheckboxTreeItem<T>({
  item,
  children,
  subtreePartiallySelected,
  subtreePartiallyDisabled,
  subtreeEntirelySelected,
  disabled,
  renderLabel,
}: CheckboxTreeItemProps<T>) {
  const classes = useStyles();

  const {
    expanded: expandedIds,
    selected: selectedIds,
    expandedSet,
    selectedSet,
    disabledSet,
    onExpandChange,
    onSelectionChange,
    noNesting,
    filterState,
    setFilterState,
  } = useCheckboxTreeContext();

  const expanded = filterState
    ? filterState.expandedNodes.has(item.id)
    : expandedSet.has(item.id);

  const selected = selectedSet.has(item.id) || subtreeEntirelySelected;

  const indeterminate = !selected && subtreePartiallySelected;

  const onCheck = (checked: boolean) => {
    handleCheck(
      checked,
      subtreePartiallyDisabled ?? false,
      indeterminate ?? false,
      item,
      selectedIds,
      disabledSet,
      onSelectionChange,
    );
  };
  // determine how to render the label with priority item.labelComponent -> renderLabel -> item.label -> item.id
  const label = renderLabel?.(item) ?? item.label ?? item.id;

  const handleExpandToggle = () => {
    // if a filter is active, change the filter expanded state instead of the main one so we can return to it after the filter is removed
    if (filterState) {
      setFilterState(old => {
        if (!old) return old;
        const filteredExpanded = Array.from(old.expandedNodes);
        const tmp = filteredExpanded.filter(id => id !== item.id);

        return {
          ...old,
          expandedNodes: new Set(
            tmp.length === filteredExpanded.length
              ? [...filteredExpanded, item.id]
              : tmp,
          ),
        };
      });
      return;
    }

    // update the main expandend array
    const tmp = expandedIds.filter(id => id !== item.id);
    onExpandChange(
      tmp.length === expandedIds.length ? [...expandedIds, item.id] : tmp,
    );
  };

  return (
    <>
      <Box className={classes.item}>
        {!noNesting && (
          <IconButton
            aria-label={expanded ? 'collapse' : 'expand'}
            className={clsx(
              classes.expandIcon,
              expanded && classes.expanded,
              !item.children.length && classes.childless,
            )}
            size="small"
            onClick={handleExpandToggle}
          >
            <KeyboardArrowRightIcon color="primary" className={classes.icon} />
          </IconButton>
        )}
        <BaseCheckbox
          label={label}
          adornment={item.adornment}
          checked={selected}
          disabled={disabled}
          indeterminate={indeterminate}
          onChange={(_e, checked) => {
            onCheck(checked);
          }}
        />
      </Box>
      <Box className={classes.itemChildren}>{expanded && children}</Box>
    </>
  );
}
export function CheckboxTree<T = undefined>({
  items,
  selected,
  expanded,
  onExpandChange,
  onSelectionChange,
  filterValue,
  renderLabel,
  disabled,
  filterFn,
}: CheckboxTreeProps<T>) {
  const getFilterState = useCallback(() => {
    if (!filterValue) return undefined;
    return filter(filterValue, items, filterFn);
  }, [filterValue, items, filterFn]);

  const [filterState, setFilterState] = useState(getFilterState());

  useEffect(() => {
    setFilterState(getFilterState());
  }, [getFilterState]);

  const { selectedSet, expandedSet, itemsWithParent, disabledSet } =
    useCheckboxTree({
      items,
      selected,
      expanded,
      disabled,
      onExpandChange,
      onSelectionChange,
    });

  const renderTree: (node: CheckboxNodeWithParent<T>) => {
    element: ReactNode;
    subtreeEntirelySelected: boolean;
    subtreePartiallySelected: boolean;
    subtreePartiallyDisabled: boolean;
    subtreeDisabled: boolean;
  } = node => {
    const hidden = filterState && !filterState.filteredNodes.has(node.id);

    const children = node.children.map(n => renderTree(n));

    const childrenPartiallySelected = children.some(
      c => c.subtreePartiallySelected,
    );

    const childrenEntirelySelected = children.length
      ? children.every(c => c.subtreeEntirelySelected)
      : selectedSet.has(node.id);

    const childrenPartiallyDisabled = children.some(
      c => c.subtreePartiallyDisabled,
    );

    const elementDisabled = disabledSet.has(node.id) || !!node.disabled;

    const subtreeDisabled =
      children.every(c => c.subtreeDisabled) && elementDisabled;

    return {
      // if the element is currently null, render nothing
      element: hidden ? null : (
        <CheckboxTreeItem
          key={node.id}
          renderLabel={renderLabel}
          item={node}
          // we disable a node only if its entire subtree is disabled, otherwise we couldn't interact with the enabled elements in its subtree
          disabled={subtreeDisabled}
          subtreePartiallyDisabled={childrenPartiallyDisabled}
          subtreePartiallySelected={childrenPartiallySelected}
          subtreeEntirelySelected={childrenEntirelySelected}
        >
          {children.map(c => c.element)}
        </CheckboxTreeItem>
      ),
      subtreeDisabled: subtreeDisabled,
      subtreePartiallySelected:
        childrenPartiallySelected || selectedSet.has(node.id),
      subtreeEntirelySelected: childrenEntirelySelected,
      subtreePartiallyDisabled: elementDisabled || childrenPartiallyDisabled,
    };
  };

  // true if none of the roots have children, we can avoid the checkbox padding on the left in this specific case
  const noNesting = !items.some(i => !!i.children.length);

  return (
    <CheckboxTreeContextProvider
      value={{
        selected,
        expanded,
        expandedSet,
        selectedSet,
        disabledSet,
        onExpandChange,
        onSelectionChange,
        filterState,
        setFilterState,
        noNesting,
      }}
    >
      {itemsWithParent.map(i => renderTree(i).element)}
    </CheckboxTreeContextProvider>
  );
}
